import React, { useState, useEffect, createContext, useContext } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import {
  Scale, Users, FileText, Calendar, Home, LogOut, Menu, X,
  Plus, Search, Edit, Trash2, Eye, BookOpen, BarChart3,
  User, Settings, ChevronDown, Building2, Clock, AlertCircle,
  CheckCircle, FileCheck, FolderOpen, UserPlus, Save, Filter, Sparkles, Shield,
  CreditCard, Receipt, CalendarClock, ShieldCheck, BookUser, Mail, PieChart,
  ClipboardList, MessageSquare, Trophy, Timer, CalendarDays, UserCog, Languages, Euro
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { it } from "date-fns/locale";

// Import pages
import LandingPage from './pages/LandingPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API Helper
const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Constants
const TIPI_ATTO = [
  { value: 'compravendita', label: 'Compravendita' },
  { value: 'mutuo', label: 'Mutuo' },
  { value: 'donazione', label: 'Donazione' },
  { value: 'procura', label: 'Procura' },
  { value: 'testamento', label: 'Testamento' },
  { value: 'successione', label: 'Successione' },
  { value: 'atto_societario', label: 'Atto Societario' },
  { value: 'altro', label: 'Altro' }
];

// Import componente notifiche
import NotificheDropdown from './components/NotificheDropdown';
import LoginPage from './pages/LoginPage';

const STATI_PRATICA = [
  // Fase 1: Apertura
  { value: 'bozza', label: 'Bozza', color: 'bg-slate-400', fase: 1, icon: 'FileEdit' },
  { value: 'in_lavorazione', label: 'In Lavorazione', color: 'bg-blue-500', fase: 1, icon: 'Loader' },
  // Fase 2: Verifiche
  { value: 'verifiche_in_corso', label: 'Verifiche in Corso', color: 'bg-amber-500', fase: 2, icon: 'Search' },
  { value: 'verifiche_completate', label: 'Verifiche OK', color: 'bg-amber-600', fase: 2, icon: 'CheckCircle' },
  // Fase 3: Redazione
  { value: 'in_redazione', label: 'In Redazione', color: 'bg-indigo-500', fase: 3, icon: 'PenTool' },
  { value: 'revisione', label: 'Revisione Notaio', color: 'bg-indigo-600', fase: 3, icon: 'Eye' },
  // Fase 4: Stipula
  { value: 'da_firmare', label: 'Da Firmare', color: 'bg-orange-500', fase: 4, icon: 'Signature' },
  { value: 'stipulato', label: 'Stipulato', color: 'bg-green-500', fase: 4, icon: 'CheckSquare' },
  // Fase 5: Registrazione
  { value: 'in_registrazione', label: 'In Registrazione', color: 'bg-purple-500', fase: 5, icon: 'Send' },
  { value: 'registrato', label: 'Registrato', color: 'bg-purple-700', fase: 5, icon: 'Award' },
  // Altri stati
  { value: 'sospeso', label: 'Sospeso', color: 'bg-gray-500', fase: 0, icon: 'Pause' },
  { value: 'annullato', label: 'Annullato', color: 'bg-red-500', fase: 0, icon: 'XCircle' }
];

const PRIORITA_PRATICA = [
  { value: 'bassa', label: 'Bassa', color: 'bg-slate-400' },
  { value: 'normale', label: 'Normale', color: 'bg-blue-500' },
  { value: 'alta', label: 'Alta', color: 'bg-orange-500' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-500' }
];

const RUOLI_PARTE = [
  'Venditore', 'Acquirente', 'Mutuatario', 'Banca', 'Donante', 'Donatario',
  'Mandante', 'Mandatario', 'Testatore', 'Erede', 'Socio', 'Altro'
];

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check permessi utente
  // Impiegato amministrativo ha sempre accesso, notaio e impiegato solo se abilitati
  const hasAdminAccess = user?.ruolo === 'superadmin' || 
                         user?.ruolo === 'impiegato_amministrativo' || 
                         user?.permessi?.accesso_amministrazione;
  const hasPecAccess = user?.ruolo === 'superadmin' || 
                       user?.permessi?.gestione_pec || 
                       (user?.permessi?.caselle_pec?.length > 0);

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/' },
    { icon: Euro, label: 'Preventivi', path: '/preventivi' },
    { icon: FileText, label: 'Pratiche', path: '/pratiche' },
    { icon: Users, label: 'Clienti', path: '/clienti' },
    { icon: BookOpen, label: 'Repertorio', path: '/repertorio' },
    { icon: Calendar, label: 'Agenda', path: '/agenda' },
  ];

  // Sezione Gestione Finanziaria (solo per chi ha accesso amministrazione)
  if (hasAdminAccess) {
    menuItems.push({ icon: CreditCard, label: 'Pagamenti', path: '/pagamenti', section: 'finance' });
    menuItems.push({ icon: Receipt, label: 'Fatturazione', path: '/fatturazione', section: 'finance' });
    menuItems.push({ icon: CalendarClock, label: 'Scadenze', path: '/scadenze', section: 'tasks' });
  }

  // Adempimenti visibile a tutti
  menuItems.push({ icon: FileCheck, label: 'Adempimenti', path: '/adempimenti', section: 'tasks' });

  // Sezione Compliance e Comunicazioni
  menuItems.push({ icon: ShieldCheck, label: 'Antiriciclaggio', path: '/antiriciclaggio', section: 'compliance' });
  
  // Email solo se ha accesso a caselle
  if (hasPecAccess) {
    menuItems.push({ icon: Mail, label: 'Gestione Email', path: '/pec', section: 'communications' });
  }
  
  menuItems.push({ icon: BookUser, label: 'Rubrica', path: '/rubrica', section: 'contacts' });

  // Report e Statistiche
  menuItems.push({ icon: PieChart, label: 'Report', path: '/report' });
  menuItems.push({ icon: BarChart3, label: 'Statistiche', path: '/statistiche' });

  // Sezione Gestione Staff (CRM interno)
  menuItems.push({ icon: ClipboardList, label: 'Task', path: '/staff/tasks', section: 'staff' });
  menuItems.push({ icon: MessageSquare, label: 'Chat Team', path: '/staff/chat', section: 'staff' });
  menuItems.push({ icon: Trophy, label: 'KPI', path: '/staff/kpi', section: 'staff' });
  menuItems.push({ icon: Timer, label: 'Timesheet', path: '/staff/timesheet', section: 'staff' });
  menuItems.push({ icon: CalendarDays, label: 'Ferie/Permessi', path: '/staff/ferie', section: 'staff' });

  // Aggiungi Strumenti AI con icona speciale
  menuItems.push({ icon: Sparkles, label: 'Strumenti AI', path: '/ai-tools', special: true });
  menuItems.push({ icon: FileText, label: 'Editor Atti', path: '/editor-atti', special: true });
  menuItems.push({ icon: Languages, label: 'Traduzioni', path: '/traduzioni', special: true });

  if (user?.ruolo === 'notaio') {
    menuItems.push({ icon: Settings, label: 'Utenti', path: '/utenti' });
  }

  if (user?.ruolo === 'superadmin') {
    menuItems.push({ icon: Settings, label: 'Utenti', path: '/utenti' });
    menuItems.push({ icon: Shield, label: 'Super Admin', path: '/superadmin', superadmin: true });
    menuItems.push({ icon: Building2, label: 'Impostazioni Studio', path: '/impostazioni-studio', superadmin: true });
    menuItems.push({ icon: Mail, label: 'Gestione Caselle', path: '/pec', superadmin: true });
    menuItems.push({ icon: Mail, label: 'Email Automatiche', path: '/email-log', superadmin: true });
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden shadow">
                <img src="/logo-notaio.jpg" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Studio Notarile AI</h1>
                <p className="text-xs text-amber-600 italic">Il futuro del notariato è qui</p>
              </div>
            </div>
            <NotificheDropdown />
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                location.pathname === item.path || 
                (item.path === '/ai-tools' && location.pathname.startsWith('/ai-tools')) ||
                (item.path.startsWith('/staff/') && location.pathname === item.path)
                  ? item.superadmin ? 'bg-red-600 text-white' : item.special ? 'bg-purple-600 text-white' : 'bg-amber-600 text-white'
                  : item.superadmin ? 'text-red-300 hover:bg-red-900/50' : item.special ? 'text-purple-300 hover:bg-purple-900/50' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <item.icon className={`w-4 h-4 ${item.special ? 'text-purple-300' : item.superadmin ? 'text-red-300' : ''}`} />
              <span>{item.label}</span>
              {item.special && <Badge className="ml-auto bg-purple-500 text-xs">AI</Badge>}
              {item.superadmin && <Badge className="ml-auto bg-red-500 text-xs">ADMIN</Badge>}
            </button>
          ))}
        </nav>
        
        <div className="flex-shrink-0 p-4 border-t border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-sm">{user?.nome} {user?.cognome}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.ruolo}</p>
            </div>
          </div>
          
          {/* Pulsante Esci - ben visibile */}
          <Button 
            variant="outline" 
            className="w-full bg-slate-800 text-white border-slate-600 hover:bg-red-600 hover:border-red-600 hover:text-white mb-3" 
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
          
          {/* Link Supporto TelcaVoIP */}
          <a 
            href="https://telcavoip.ai/it/area-clienti/login" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 px-3 text-xs text-slate-400 hover:text-amber-400 transition-colors"
          >
            <AlertCircle className="w-3 h-3" />
            <span>Assistenza TelcaVoIP</span>
          </a>
        </div>
      </aside>
    </>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-30 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </Button>
        <img src="/logo-notaio.jpg" alt="Logo" className="w-8 h-8 ml-2 rounded-full object-cover" />
        <span className="ml-2 font-bold">Studio Notarile AI</span>
      </header>
      
      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>;

  const statCards = [
    { label: 'Pratiche Totali', value: stats?.pratiche?.totali || 0, icon: FileText, color: 'bg-blue-500' },
    { label: 'In Lavorazione', value: stats?.pratiche?.in_lavorazione || 0, icon: Clock, color: 'bg-yellow-500' },
    { label: 'Da Firmare', value: stats?.pratiche?.da_firmare || 0, icon: AlertCircle, color: 'bg-orange-500' },
    { label: 'Clienti', value: stats?.clienti_totali || 0, icon: Users, color: 'bg-green-500' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <Button onClick={() => navigate('/pratiche/nuova')}>
          <Plus className="w-4 h-4 mr-2" /> Nuova Pratica
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prossimi Appuntamenti */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Prossimi Appuntamenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.prossimi_appuntamenti?.length > 0 ? (
              <div className="space-y-3">
                {stats.prossimi_appuntamenti.map((app, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{app.titolo}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(app.data_ora), "dd MMM yyyy HH:mm", { locale: it })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Nessun appuntamento programmato</p>
            )}
          </CardContent>
        </Card>

        {/* Ultime Pratiche */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Ultime Pratiche
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.ultime_pratiche?.length > 0 ? (
              <div className="space-y-3">
                {stats.ultime_pratiche.map((pratica, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100" onClick={() => navigate(`/pratiche/${pratica.id}`)}>
                    <div>
                      <p className="font-medium">{pratica.numero_pratica}</p>
                      <p className="text-sm text-muted-foreground">{pratica.oggetto}</p>
                    </div>
                    <Badge className={STATI_PRATICA.find(s => s.value === pratica.stato)?.color}>
                      {STATI_PRATICA.find(s => s.value === pratica.stato)?.label}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Nessuna pratica</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Export all components
export {
  LoginPage,
  DashboardPage,
  Layout,
  api,
  TIPI_ATTO,
  STATI_PRATICA,
  PRIORITA_PRATICA,
  RUOLI_PARTE
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const login = (data) => {
    setToken(data.access_token);
    setUser(data.user);
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

// App Component
function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AppRoutes />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

// Lazy load pages
const ClientiPage = React.lazy(() => import('./pages/ClientiPage'));
const PratichePage = React.lazy(() => import('./pages/PratichePage'));
const PraticaDetailPage = React.lazy(() => import('./pages/PraticaDetailPage'));
const RepertorioPage = React.lazy(() => import('./pages/RepertorioPage'));
const AgendaPage = React.lazy(() => import('./pages/AgendaPage'));
const StatistichePage = React.lazy(() => import('./pages/StatistichePage'));
const UtentiPage = React.lazy(() => import('./pages/UtentiPage'));
const AIToolsPage = React.lazy(() => import('./pages/AIToolsPage'));
const SuperAdminPage = React.lazy(() => import('./pages/SuperAdminPage'));
// Nuove pagine gestione studio
const PagamentiPage = React.lazy(() => import('./pages/PagamentiPage'));
const FatturazionePage = React.lazy(() => import('./pages/FatturazionePage'));
const ScadenzePage = React.lazy(() => import('./pages/ScadenzePage'));
const AdempimentiPage = React.lazy(() => import('./pages/AdempimentiPage'));
const AntiriciclaggioPage = React.lazy(() => import('./pages/AntiriciclaggioPage'));
const RubricaPage = React.lazy(() => import('./pages/RubricaPage'));
const PreventiviPage = React.lazy(() => import('./pages/PreventiviPage'));
// Nuove pagine PEC, Report, Dashboard
const PecPage = React.lazy(() => import('./pages/PecPage'));
const ReportPage = React.lazy(() => import('./pages/ReportPage'));
const DashboardPersonalizzabile = React.lazy(() => import('./pages/DashboardPersonalizzabile'));
// Pagine Staff Management (CRM interno)
const TasksPage = React.lazy(() => import('./pages/TasksPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const KpiPage = React.lazy(() => import('./pages/KpiPage'));
const TimesheetPage = React.lazy(() => import('./pages/TimesheetPage'));
const FeriePage = React.lazy(() => import('./pages/FeriePage'));
const ImpostazioniStudioPage = React.lazy(() => import('./pages/ImpostazioniStudioPage'));
const EmailLogPage = React.lazy(() => import('./pages/EmailLogPage'));
const TraduzioneDocumentiPage = React.lazy(() => import('./pages/TraduzioneDocumentiPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const EditorAttiPage = React.lazy(() => import('./pages/EditorAttiPage'));
const SubscriptionSuccessPage = React.lazy(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionCancelPage = React.lazy(() => import('./pages/SubscriptionCancelPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));

// Import CookieConsent component
import CookieConsent from './components/CookieConsent';

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>}>
      <Routes>
        {/* Public Routes */}
        <Route path="/home" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
        
        {/* Redirect root to landing or dashboard based on auth */}
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPersonalizzabile /></ProtectedRoute>} />
        <Route path="/clienti" element={<ProtectedRoute><ClientiPage /></ProtectedRoute>} />
        <Route path="/preventivi" element={<ProtectedRoute><PreventiviPage /></ProtectedRoute>} />
        <Route path="/pratiche" element={<ProtectedRoute><PratichePage /></ProtectedRoute>} />
        <Route path="/pratiche/nuova" element={<ProtectedRoute><PraticaDetailPage /></ProtectedRoute>} />
        <Route path="/pratiche/:id" element={<ProtectedRoute><PraticaDetailPage /></ProtectedRoute>} />
        <Route path="/editor-atti" element={<ProtectedRoute><EditorAttiPage /></ProtectedRoute>} />
        <Route path="/editor-atti/:praticaId" element={<ProtectedRoute><EditorAttiPage /></ProtectedRoute>} />
        <Route path="/repertorio" element={<ProtectedRoute><RepertorioPage /></ProtectedRoute>} />
        <Route path="/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
        <Route path="/statistiche" element={<ProtectedRoute><StatistichePage /></ProtectedRoute>} />
        <Route path="/utenti" element={<ProtectedRoute><UtentiPage /></ProtectedRoute>} />
        <Route path="/ai-tools" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
        <Route path="/ai-tools/:praticaId" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
        <Route path="/superadmin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
        {/* Nuove route gestione studio */}
        <Route path="/pagamenti" element={<ProtectedRoute><PagamentiPage /></ProtectedRoute>} />
        <Route path="/fatturazione" element={<ProtectedRoute><FatturazionePage /></ProtectedRoute>} />
        <Route path="/scadenze" element={<ProtectedRoute><ScadenzePage /></ProtectedRoute>} />
        <Route path="/adempimenti" element={<ProtectedRoute><AdempimentiPage /></ProtectedRoute>} />
        <Route path="/antiriciclaggio" element={<ProtectedRoute><AntiriciclaggioPage /></ProtectedRoute>} />
        <Route path="/rubrica" element={<ProtectedRoute><RubricaPage /></ProtectedRoute>} />
        {/* PEC, Report */}
        <Route path="/pec" element={<ProtectedRoute><PecPage /></ProtectedRoute>} />
        <Route path="/report" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
        {/* Staff Management Routes */}
        <Route path="/staff/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/staff/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/staff/kpi" element={<ProtectedRoute><KpiPage /></ProtectedRoute>} />
        <Route path="/staff/timesheet" element={<ProtectedRoute><TimesheetPage /></ProtectedRoute>} />
        <Route path="/staff/ferie" element={<ProtectedRoute><FeriePage /></ProtectedRoute>} />
        <Route path="/impostazioni-studio" element={<ProtectedRoute><ImpostazioniStudioPage /></ProtectedRoute>} />
        <Route path="/email-log" element={<ProtectedRoute><EmailLogPage /></ProtectedRoute>} />
        <Route path="/traduzioni" element={<ProtectedRoute><TraduzioneDocumentiPage /></ProtectedRoute>} />
      </Routes>
    </React.Suspense>
  );
};

export default App;
